/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars -- Step logic relies on evolving API/contracts; safe cleanup deferred. */
import React, { useCallback, useEffect, useMemo, useState } from 'react'; // Components
import {
  Badge,
  Button,
  CardHeader,
  Divider,
  Flex,
  SegmentedControl,
  Text,
  TextField,
  VisuallyHidden,
} from '@/components/ui';
import {
  IconAlertTriangle,
  IconCheck,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconSearch,
  IconShieldCheck,
  IconX,
} from '@tabler/icons-react'; // Custom Components
import { FormHierarchyPreviewTree } from '../FormHierarchyPreviewTree';
import { DataPreviewTable } from '../DataPreviewTable';
import {
  ValidationErrorDetails,
  ValidationErrorsPanel,
} from '@/components/data-import'; // API
import {
  createImportMapping,
  executeDryRunValidation,
} from '@/services/importMappingsApi';

// Utils
import { transformWizardStateToApiRequest } from '../../utils/mappingTransformer';

// Types
import { StepComponentProps } from '../types';
import { AxiosError } from 'axios';

type PreviewAndValidationStepProps = StepComponentProps;

export const PreviewAndValidationStep: React.FC<
  PreviewAndValidationStepProps
> = ({
  wizardState,
  onUpdateState,
  onNext,
  onPrevious,
  canGoNext,
  canGoPrevious,
  currentStep,
  totalSteps,
}) => {
  const {
    availableForms,
    fileToFormMappings,
    columnMappings,
    selectedFiles,
    subformRelationships,
  } = wizardState;

  const [isValidating, setIsValidating] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [validationResults, setValidationResults] = useState<any>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState<'preview' | 'errors'>('preview');

  // Get validation errors from wizard state (set by handleCreateMapping in StudyDataImportWizard)
  const validationErrors = wizardState.validationErrors as
    | ValidationErrorDetails
    | null
    | undefined;

  // Count total errors
  const errorCount = useMemo(() => {
    if (!validationErrors) return 0;
    let count = 0;
    if (validationErrors.column_mappings) {
      Object.values(validationErrors.column_mappings).forEach(fieldErrors => {
        Object.values(fieldErrors).forEach(messages => {
          count += Array.isArray(messages) ? messages.length : 1;
        });
      });
    }
    if (validationErrors.file_to_form_mappings) {
      Object.values(validationErrors.file_to_form_mappings).forEach(
        fieldErrors => {
          Object.values(fieldErrors).forEach(messages => {
            count += Array.isArray(messages) ? messages.length : 1;
          });
        }
      );
    }
    if (validationErrors.subform_relationships) {
      Object.values(validationErrors.subform_relationships).forEach(
        fieldErrors => {
          Object.values(fieldErrors).forEach(messages => {
            count += Array.isArray(messages) ? messages.length : 1;
          });
        }
      );
    }
    return count;
  }, [validationErrors]);

  // Switch to errors view when errors appear
  React.useEffect(() => {
    if (validationErrors) {
      setViewMode('errors');
    }
  }, [validationErrors]);

  // Debug: Log validation errors when they change
  useEffect(() => {
    if (validationErrors) {
      console.log('ValidationErrors from wizard state:', validationErrors);
    }
  }, [validationErrors]);
  const [selectedFormId, setSelectedFormId] = useState<number | undefined>(
    undefined
  );
  const [selectedFileId, setSelectedFileId] = useState<string | undefined>(
    undefined
  );
  const [selectedFormNestedPath, setSelectedFormNestedPath] = useState<
    string | undefined
  >(undefined);

  // Calculate simple statistics
  const stats = useMemo(() => {
    const totalFiles = selectedFiles.length;
    const mappedFiles = fileToFormMappings.length;
    const totalColumns = columnMappings.length;
    const formFieldMappings = columnMappings.filter(
      cm => cm.mappingType === 'formField'
    ).length;
    const metadataMappings = columnMappings.filter(
      cm => cm.mappingType === 'metadata'
    ).length;
    const ignoredMappings = columnMappings.filter(
      cm => cm.mappingType === 'ignored'
    ).length;

    return {
      totalFiles,
      mappedFiles,
      totalColumns,
      formFieldMappings,
      metadataMappings,
      ignoredMappings,
      isComplete: mappedFiles === totalFiles && totalFiles > 0,
    };
  }, [selectedFiles, fileToFormMappings, columnMappings]);

  // Get selected form and file
  const selectedForm = useMemo(() => {
    return selectedFormId
      ? availableForms?.find(f => f.id === selectedFormId)
      : undefined;
  }, [selectedFormId, availableForms]);

  const selectedFile = useMemo(() => {
    if (!selectedFileId) return undefined;
    return selectedFiles.find(file => {
      const fileId =
        file.source === 'uploaded'
          ? file.file!.name
          : `csv-${file.csvFile!.id}`;
      return fileId === selectedFileId;
    });
  }, [selectedFileId, selectedFiles]);

  // Get the form for the selected file
  const selectedFileForm = useMemo(() => {
    if (!selectedFile || !selectedFileId) return undefined;
    const formMapping = fileToFormMappings.find(
      m => m.fileId === selectedFileId
    );
    return formMapping
      ? availableForms?.find(f => f.id === formMapping.formId)
      : undefined;
  }, [selectedFile, selectedFileId, fileToFormMappings, availableForms]);

  // Handle form selection
  const handleFormSelect = useCallback((formId: number, nestedPath?: string) => {
    setSelectedFormId(formId);
    setSelectedFormNestedPath(nestedPath);
    setSelectedFileId(undefined); // Clear file selection when form is selected
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((fileId: string) => {
    setSelectedFileId(fileId);
    // When file is selected, also set the nested_path from the file's mapping
    const fileMapping = fileToFormMappings.find(m => m.fileId === fileId);
    if (fileMapping?.nested_path) {
      setSelectedFormNestedPath(fileMapping.nested_path);
    } else {
      setSelectedFormNestedPath(undefined);
    }
    // Don't clear form selection - keep it for context
  }, [fileToFormMappings]);

  // Handle data validation (dry-run)
  const handleValidateData = useCallback(async () => {
    setIsValidating(true);
    // Clear previous errors from wizard state
    onUpdateState({ validationErrors: undefined });

    let hasValidationErrors = false;

    try {
      let mappingId = wizardState.importMappingId;

      // If no mapping exists, create one first
      if (!mappingId) {
        try {
          const mappingRequest = transformWizardStateToApiRequest(wizardState);
          const createdMapping = await createImportMapping(mappingRequest);
          mappingId = createdMapping.id;

          // Save the mapping ID to wizard state
          onUpdateState({ importMappingId: mappingId });
        } catch (createError) {
          // Handle validation errors from mapping creation
          console.log('Create mapping error:', createError);
          if (createError instanceof AxiosError) {
            console.log('Error response:', createError.response?.data);
            console.log('Error status:', createError.response?.status);
            console.log(
              'Error details path:',
              createError.response?.data?.error?.details
            );

            // Try different possible error structures
            const errorData = createError.response?.data;
            let errorDetails: ValidationErrorDetails | null = null;

            if (errorData?.error?.details) {
              errorDetails = errorData.error.details as ValidationErrorDetails;
            } else if (errorData?.details) {
              errorDetails = errorData.details as ValidationErrorDetails;
            } else if (
              errorData?.error &&
              typeof errorData.error === 'object'
            ) {
              // Check if error itself contains details
              errorDetails = errorData.error as ValidationErrorDetails;
            }

            if (errorDetails) {
              console.log(
                'Setting validation errors in wizard state:',
                errorDetails
              );
              onUpdateState({ validationErrors: errorDetails });
              hasValidationErrors = true;

              // Set validation results to show error state
              setValidationResults({
                isValid: false,
                errors: [
                  'Mapping validation failed. Please review the errors below and fix them before proceeding.',
                ],
                warnings: [],
                summary: {
                  totalFiles: stats.totalFiles,
                  mappedFiles: stats.mappedFiles,
                  totalColumns: stats.totalColumns,
                  formFieldMappings: stats.formFieldMappings,
                  metadataMappings: stats.metadataMappings,
                },
              });

              // Don't throw error if we have validation details - just stop here
              return;
            } else {
              console.log('No validation error details found in response');
            }
          }
          // If no validation details found, throw the error
          throw createError;
        }
      }

      // Execute dry-run validation
      const dryRunResult = await executeDryRunValidation(mappingId, {
        validate_data: true,
        generate_preview: true,
        max_preview_rows: 10,
      });

      // Convert API response to component format
      const results = {
        isValid: dryRunResult.is_valid,
        errors: dryRunResult.errors.map(err => err.message),
        warnings: dryRunResult.warnings.map(warn => warn.message),
        summary: {
          totalFiles: dryRunResult.summary.total_files,
          mappedFiles: dryRunResult.summary.mapped_files,
          totalColumns: dryRunResult.summary.total_columns,
          formFieldMappings: dryRunResult.summary.form_field_mappings,
          metadataMappings: dryRunResult.summary.metadata_mappings,
        },
      };

      setValidationResults(results);
    } catch (error) {
      // Check if this is a validation error we've already handled
      if (hasValidationErrors) {
        // Validation errors already set, just set generic validation results
        setValidationResults({
          isValid: false,
          errors: [
            'Mapping validation failed. Please review the errors below and fix them before proceeding.',
          ],
          warnings: [],
          summary: {
            totalFiles: stats.totalFiles,
            mappedFiles: stats.mappedFiles,
            totalColumns: stats.totalColumns,
            formFieldMappings: stats.formFieldMappings,
            metadataMappings: stats.metadataMappings,
          },
        });
      } else {
        // Handle other errors (network, etc.)
        setValidationResults({
          isValid: false,
          errors: [
            'Dry-run validation failed. Please check your mappings and try again.',
          ],
          warnings: [],
          summary: {
            totalFiles: stats.totalFiles,
            mappedFiles: stats.mappedFiles,
            totalColumns: stats.totalColumns,
            formFieldMappings: stats.formFieldMappings,
            metadataMappings: stats.metadataMappings,
          },
        });
      }
    } finally {
      setIsValidating(false);
    }
  }, [wizardState, onUpdateState, stats]);

  return (
    <>
      {/* Header */}
      <CardHeader
        cardTitle="Preview & Validation"
        description="Review mappings and validate data before import"
        size="large"
      />

      <Flex justify="start" align="center" gap="2">
        <Button
          variant="surface"
          isIconOnly
          size="small"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          {sidebarCollapsed ? (
            <IconLayoutSidebarLeftExpand />
          ) : (
            <IconLayoutSidebarLeftCollapse />
          )}
        </Button>
        <Flex>
          <TextField
            placeholder="Search files by name"
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
            }}
            startSlot={<IconSearch />}
            size="small"
            className="data-table-toolbar__search-input"
            // Reset filter button
            endSlot={
              searchQuery && (
                <Button
                  variant="soft"
                  size="xsmall"
                  isIconOnly
                  color="error"
                  className="data-table-toolbar__reset"
                  title={'Reset'}
                  onClick={() => {}}
                >
                  <IconX />
                </Button>
              )
            }
          />
        </Flex>

        {/* View mode switcher - show only if errors exist */}
        {validationErrors && (
          <SegmentedControl
            items={[
              { value: 'preview', label: 'Preview' },
              {
                value: 'errors',
                label: (
                  <Flex align="center" gap="1">
                    <Text size="inherit">Errors</Text>
                    <Badge size="xsmall" color="error" variant="soft">
                      {errorCount}
                    </Badge>
                  </Flex>
                ),
              },
            ]}
            value={viewMode}
            onValueChange={(value: string) =>
              setViewMode(value as 'preview' | 'errors')
            }
            size="small"
          />
        )}

        {/* Statistics Summary */}
        <Flex gap="2" wrap="wrap" justify="start" ml="auto">
          <Badge highContrast size="xsmall">
            Total Files: {stats.totalFiles}
          </Badge>
          <Badge size="xsmall">Mapped Files: {stats.mappedFiles}</Badge>
          <Badge size="xsmall">Total Columns: {stats.totalColumns}</Badge>
          <Badge size="xsmall">Form Fields: {stats.formFieldMappings}</Badge>
          <Badge size="xsmall">Metadata: {stats.metadataMappings}</Badge>

          <Flex gap="1" ml="4">
            <Text size="2xsmall">Status:</Text>
            <Badge size="xsmall" color="success" variant="surface">
              {stats.mappedFiles} Mapped
            </Badge>
            {stats.ignoredMappings > 0 && (
              <Badge size="xsmall" color="warning" variant="surface">
                {stats.ignoredMappings} Ignored
              </Badge>
            )}
          </Flex>
        </Flex>
        <Flex gap="2" align="center">
          <Badge
            color={stats.isComplete ? 'success' : 'warning'}
            variant="surface"
            size="small"
            startIcon={stats.isComplete ? <IconCheck /> : <IconAlertTriangle />}
          >
            {stats.isComplete ? 'Ready for Import' : 'Needs Review'}
          </Badge>
          <VisuallyHidden>
            <Button
              variant="surface"
              color="primary"
              onClick={handleValidateData}
              loading={isValidating}
              size="small"
              startIcon={<IconShieldCheck />}
            >
              {isValidating ? 'Validating...' : 'Dry Run'}
            </Button>
          </VisuallyHidden>
        </Flex>
      </Flex>

      <Flex justify="between" align="center">
        <VisuallyHidden>
          <Flex direction="column" gap="1">
            <Text size="5" weight="bold">
              Step {currentStep} of {totalSteps}: Preview & Validation
            </Text>
          </Flex>
        </VisuallyHidden>
      </Flex>

      {/* Main Content */}
      <Flex
        flexGrow="1"
        // style={{ height: 'calc(100vh - 300px)', minHeight: '600px' }}
      >
        {/* Left Sidebar - Form Hierarchy */}
        <div
          style={{
            width: sidebarCollapsed ? '0px' : '350px',
            transition: 'width 0.3s ease',
            flexShrink: 0,
          }}
        >
          <FormHierarchyPreviewTree
            forms={availableForms || []}
            fileToFormMappings={fileToFormMappings}
            selectedFiles={selectedFiles}
            columnMappings={columnMappings}
            selectedFormId={selectedFormId}
            selectedFileId={selectedFileId}
            selectedFormNestedPath={selectedFormNestedPath}
            onFormSelect={handleFormSelect}
            onFileSelect={handleFileSelect}
          />
        </div>

        <Divider orientation="vertical" mx="2" />

        {/* Right Panel - Data Preview or Errors */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {viewMode === 'errors' && validationErrors ? (
            <ValidationErrorsPanel
              errors={validationErrors}
              columnMappings={columnMappings}
              selectedFiles={selectedFiles}
              fileToFormMappings={fileToFormMappings}
            />
          ) : (
            <DataPreviewTable
              selectedForm={selectedFile ? selectedFileForm : selectedForm}
              selectedFile={selectedFile}
              selectedFileId={selectedFileId}
              selectedFormNestedPath={selectedFormNestedPath}
              forms={availableForms || []}
              fileToFormMappings={fileToFormMappings}
              columnMappings={columnMappings}
              selectedFiles={selectedFiles}
              onValidateData={handleValidateData}
              isValidating={isValidating}
              validationResults={validationResults}
            />
          )}
        </div>
      </Flex>
    </>
  );
};
